/**
 * S-01 · Cierre estructurado (tanda EMT-CNV-2026-01) — helpers PUROS de la UI.
 *
 * El técnico cierra EN SITIO (modelo + número de serie + resultado) y el
 * trabajo queda "Por validar". Supervisión completa lo COMERCIAL desde el panel
 * (monto, forma de pago, vendedor) y recién ahí el trabajo queda cerrado en
 * firme.
 *
 * Las reglas duras viven en el backend; esto es para que la pantalla pueda
 * decirle al usuario QUÉ FALTA antes de que apriete el botón, en llano.
 */

/** Datos del cierre que mira la validación de supervisión. */
export interface CierreComercial {
  esGarantia: boolean;
  requiereComprobanteFiscal: boolean;
  montoCierre?: number | null;
  formaPago?: string | null;
  vendedorUserId?: string | null;
  rnc?: string | null;
  comprobanteFiscal?: string | null;
}

/** ¿Hay texto real (no solo espacios)? */
const lleno = (v?: string | null): boolean => typeof v === 'string' && v.trim() !== '';

/**
 * Qué le falta al trabajo para poder cerrarse en firme, en el idioma del dueño.
 * Devuelve [] cuando no falta nada.
 *
 * Las GARANTÍAS quedan exentas de monto/pago/vendedor: no hay venta. El
 * comprobante fiscal es ORTOGONAL a eso — si el cliente lo pidió, hace falta
 * igual (una garantía también puede necesitar comprobante).
 */
export function camposFaltantesValidacion(cierre: CierreComercial): string[] {
  const faltan: string[] = [];

  if (!cierre.esGarantia) {
    // Un monto de 0 es un monto: solo falta si viene vacío.
    if (cierre.montoCierre == null || !Number.isFinite(cierre.montoCierre)) {
      faltan.push('el monto cobrado');
    }
    if (!lleno(cierre.formaPago)) faltan.push('la forma de pago');
    if (!lleno(cierre.vendedorUserId)) faltan.push('el vendedor');
  }

  if (cierre.requiereComprobanteFiscal) {
    if (!lleno(cierre.rnc)) faltan.push('el RNC del cliente');
    if (!lleno(cierre.comprobanteFiscal)) faltan.push('el número de comprobante fiscal');
  }

  return faltan;
}

/** ¿Supervisión ya puede cerrar el trabajo en firme? */
export function puedeValidar(cierre: CierreComercial): boolean {
  return camposFaltantesValidacion(cierre).length === 0;
}

/** Lo que el TÉCNICO tiene que llenar en sitio para poder cerrar. */
export interface CierreEnSitio {
  modelo: string;
  numeroSerie: string;
  resultado: string;
}

/**
 * Mínimo de caracteres del relato de cierre ("Resultado del trabajo").
 *
 * No es un número con pretensiones: es el corte más bajo que deja afuera el
 * "ok" y el "listo" que el cliente señaló en la llamada como lo que de verdad
 * escribían los técnicos. La regla que MANDA vive en el servidor
 * (`MINIMO_RELATO_DE_CIERRE` en services/job.service.ts); acá está el mismo
 * número para poder avisarle antes de que apriete el botón, estando en la
 * puerta de un cliente y con datos móviles.
 */
export const MINIMO_RELATO_DE_CIERRE = 10;

/**
 * Qué le falta al técnico para poder cerrar, en su idioma. `null` = nada.
 *
 * El relato corto se trata aparte de los otros dos a propósito: "falta el
 * resultado" no le dice nada a alguien que ya escribió "ok" — hay que decirle
 * QUÉ escribir.
 */
export function faltaParaCerrarEnSitio(cierre: CierreEnSitio): string | null {
  if (!lleno(cierre.modelo)) return 'Elija el modelo que instaló.';
  if (!lleno(cierre.numeroSerie)) return 'Ponga el número de serie del equipo.';
  if (cierre.resultado.trim().length < MINIMO_RELATO_DE_CIERRE) {
    return 'Cuente en una línea qué hizo y cómo quedó el trabajo.';
  }
  return null;
}

/** ¿El técnico ya puede apretar "Terminé"? */
export function puedeCerrarEnSitio(cierre: CierreEnSitio): boolean {
  return faltaParaCerrarEnSitio(cierre) === null;
}

/**
 * Período de cobertura en el idioma del dueño. Se guarda en MESES (es lo que
 * permite calcular la fecha exacta), pero él piensa en años: cuando el período
 * cierra en años redondos se muestran los dos.
 */
export function etiquetaCobertura(meses: number): string {
  if (meses === 1) return '1 mes de garantía';
  if (meses % 12 === 0) {
    const anios = meses / 12;
    return `${meses} meses (${anios} ${anios === 1 ? 'año' : 'años'}) de garantía`;
  }
  return `${meses} meses de garantía`;
}
